import { Dispatch, SetStateAction, useEffect, useState } from "react"
import { Label } from "../common/label"
import useTranslator from "@pma/hooks/use-translator"
import axios from "axios"
import { ROLE_EDIT, ROLE_SET, SERVER_URL } from "@pma/utils/api-routes"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { Button } from "../common/button"
import { toast } from "@pma/hooks/use-toast"
import { useAuth } from "@pma/stores/auth-store"
import { useQueryClient } from "@tanstack/react-query"
import { cn } from "@pma/utils/helpers"
import { CircleCheckBig, CircleX } from "lucide-react"

interface Props {
  projectId: number
  userId: number
  memberAccess: {
    id: number
    project_id: number
    user_id: number
    deny_access: boolean
    view_project: boolean
    edit_project: boolean
    view_tasks: boolean
    edit_tasks: boolean
    contractor: boolean
    designer: boolean
    financial: boolean
    supervisor: boolean
  } | null
  setUserList: Dispatch<SetStateAction<any[]>>
}

type PermissionKey =
  | "view_project"
  | "edit_project"
  | "view_tasks"
  | "edit_tasks"
  | "deny_access"
  | "contractor"
  | "designer"
  | "financial"
  | "supervisor"

function ProjectPermissions({ projectId, userId, memberAccess, setUserList }: Props) {
  const { translate, locale } = useTranslator()
  const { authConfig } = useAuth()
  const { tabs } = authConfig
  const isHebrew = locale === "he"
  const queryClient = useQueryClient()

  const [changeHappened, setChangeHappened] = useState(false)
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const [permissions, setPermissions] = useState<{
    view_project: boolean | null
    edit_project: boolean | null
    view_tasks: boolean | null
    edit_tasks: boolean | null
    deny_access: boolean | null
    contractor: boolean | null
    designer: boolean | null
    financial: boolean | null
    supervisor: boolean | null
  }>({
    view_project: false,
    edit_project: false,
    view_tasks: false,
    edit_tasks: false,
    deny_access: false,
    contractor: false,
    designer: false,
    financial: false,
    supervisor: false,
  })

  const handleChange = (name: string, checked: "yes" | "no" | null) => {
    const val = { ...permissions }

    // Convert "yes" / "no" / null to boolean or null
    const boolValue = checked === "yes" ? true : checked === "no" ? false : null

    if (name === "deny_access") {
      if (boolValue === true) {
        val["view_tasks"] = false
        val["edit_tasks"] = false
        val["view_project"] = false
        val["edit_project"] = false
        val["deny_access"] = true
      } else val["deny_access"] = false
    } else {
      if (name === "edit_tasks") {
        if (boolValue === true) val["view_tasks"] = true
        val["edit_tasks"] = boolValue
      } else if (name === "view_tasks") {
        if (boolValue === false) val["edit_tasks"] = false
        val["view_tasks"] = boolValue
      } else if (name === "edit_project") {
        if (boolValue === true) val["view_project"] = true
        val["edit_project"] = boolValue
      } else if (name === "view_project") {
        if (boolValue === false) val["edit_project"] = false
        val["view_project"] = boolValue
      } else if (name === "supervisor") {
        if (boolValue === false) val["supervisor"] = false
        val["supervisor"] = boolValue
      } else if (name === "contractor") {
        if (boolValue === false) val["contractor"] = false
        val["contractor"] = boolValue
      } else if (name === "designer") {
        if (boolValue === false) val["designer"] = false
        val["designer"] = boolValue
      } else if (name === "financial") {
        if (boolValue === false) val["financial"] = false
        val["financial"] = boolValue
      }

      if (permissions.deny_access) val["deny_access"] = false
    }

    setPermissions(val)
    setChangeHappened(true)
  }

  const setAccess = async () => {
    let response: any = ""

    const payload: any = {
      project_id: projectId,
      user_id: userId,
      deny_access: permissions.deny_access,
      view_project: permissions.view_project,
      edit_project: permissions.edit_project,
      view_tasks: permissions.view_tasks,
      edit_tasks: permissions.edit_tasks,
      contractor: permissions.contractor,
      designer: permissions.designer,
      financial: permissions.financial,
      supervisor: permissions.supervisor,
    }

    if (memberAccess) {
      payload["access_role_id"] = memberAccess.id

      response = await axios
        .post(SERVER_URL + ROLE_EDIT, payload, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        })
        .then((res) => {
          if (res.status === 200) {
            queryClient.invalidateQueries({ queryKey: ["get-project"] })
            queryClient.invalidateQueries({ queryKey: ["workspace-contractors"] })
            queryClient.invalidateQueries({ queryKey: ["organization-role-details"] })
            queryClient.invalidateQueries({ queryKey: ["get-project-wise-contractors-list"] })
            queryClient.invalidateQueries({ queryKey: ["user-list"] })
            if (setUserList) {
              setUserList((pre: any) => {
                const newData = pre.map((item: any) => {
                  if (item?.user?._role?.id == res.data.id) {
                    return { ...item, user: { ...item.user, _role: res.data } }
                  }

                  return item
                })
                return newData
              })
            }
            toast({
              title: translate("Success"),
              description: translate("User role updated"),
              variant: "success",
            })
          } else {
          }
        })
        .catch((error) => {
          const message = error.response.data.message
          toast({
            title: translate("Error"),
            description: message,
            variant: "error",
          })
        })
      resetChange()
    } else {
      response = await axios
        .post(SERVER_URL + ROLE_SET, payload, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        })
        .then((res) => {
          if (res.status === 200) {
            queryClient.invalidateQueries({ queryKey: ["get-project"] })
            queryClient.invalidateQueries({ queryKey: ["workspace-contractors"] })
            queryClient.invalidateQueries({ queryKey: ["organization-role-details"] })
            queryClient.invalidateQueries({ queryKey: ["get-project-wise-contractors-list"] })
            queryClient.invalidateQueries({ queryKey: ["user-list"] })
            toast({
              title: translate("Success"),
              description: translate("User role updated"),
              variant: "success",
            })
          }
        })
        .catch((error) => {
          const message = error.response.data.message
          toast({
            title: translate("Error"),
            description: message,
            variant: "error",
          })
          resetChange()
        })
    }

    if (tabs["project-detail"]?.main == "engineering") {
      await queryClient.invalidateQueries({ queryKey: ["contractor-context"] })
    }

    setChangeHappened(false)
  }

  const handleSetValue = () => {
    if (memberAccess) {
      setPermissions({
        view_project: memberAccess.view_project,
        edit_project: memberAccess.edit_project,
        view_tasks: memberAccess.view_tasks,
        edit_tasks: memberAccess.edit_tasks,
        deny_access: memberAccess.deny_access,
        contractor: memberAccess.contractor || false,
        designer: memberAccess.designer || false,
        financial: memberAccess.financial || false,
        supervisor: memberAccess.supervisor || false,
      })
    } else {
      setPermissions({
        view_project: null,
        edit_project: null,
        view_tasks: null,
        edit_tasks: null,
        deny_access: null,
        contractor: false,
        designer: false,
        financial: false,
        supervisor: false,
      })
    }
  }

  const resetChange = () => {
    handleSetValue()
    setChangeHappened(false)
  }

  useEffect(() => {
    handleSetValue()
  }, [memberAccess])

  const RenderHeader = ({ title }: { title: string }) => {
    return (
      <div className='flex w-full items-center justify-center'>
        <div className='flex w-full items-center'>
          {/* Left line */}
          <div className='flex-1 border-t border-gray-300'></div>

          {/* Badge */}
          <div className='mx-4 rounded-full bg-gray-400 px-4 py-1 text-xs font-semibold text-white'>
            {translate(title)}
          </div>

          {/* Right line */}
          <div className='flex-1 border-t border-gray-300'></div>
        </div>
      </div>
    )
  }

  const RenderBody = ({
    title,
    option,
    showNull = true,
  }: {
    title: string
    option: PermissionKey
    showNull?: boolean
  }) => {
    return (
      <div className='flex flex-col items-center gap-1'>
        <Label className='flex flex-col items-center text-xs font-semibold'>
          {translate(title)}
        </Label>
        <div className='rounded-base shadow-xs inline-flex -space-x-px' role='group'>
          <button
            type='button'
            className={cn(
              "text-body bg-neutral-primary-soft border-default hover:bg-neutral-secondary-medium hover:text-heading focus:ring-3 focus:ring-neutral-tertiary-soft rounded-s-lg border px-2.5 py-1.5 text-xs font-medium leading-5 focus:outline-none",
              permissions[option] ? "bg-gray-200 font-bold" : "",
            )}
            onClick={() => handleChange(option, "yes")}
          >
            {translate("Yes")}
          </button>
          <button
            type='button'
            className={cn(
              "text-body bg-neutral-primary-soft border-default hover:bg-neutral-secondary-medium hover:text-heading focus:ring-3 focus:ring-neutral-tertiary-soft border px-2.5 py-2 text-xs font-medium leading-5 focus:outline-none",
              permissions[option] == false ? "bg-gray-200 font-bold" : "",
              showNull == false ? "rounded-e-lg" : "",
            )}
            onClick={() => handleChange(option, "no")}
          >
            {translate("No")}
          </button>
          {showNull ? (
            <button
              type='button'
              className={cn(
                "text-body bg-neutral-primary-soft border-default hover:bg-neutral-secondary-medium hover:text-heading focus:ring-3 focus:ring-neutral-tertiary-soft rounded-e-lg border px-2.5 py-2 text-xs font-medium leading-5 focus:outline-none",
                permissions[option] == null ? "bg-gray-200 font-bold" : "",
              )}
              onClick={() => handleChange(option, null)}
            >
              {translate("N/A")}
            </button>
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Project Permission Start */}
      <RenderHeader title='Project permissions' />
      <div className='flex w-full flex-col justify-center gap-2'>
        <div className='flex flex-col gap-4'>
          <div className='flex-row'>
            <div className='flex flex-col justify-around gap-3 lg:flex-row'>
              <RenderBody title='Can view' option='view_project' />
              <RenderBody title='Can edit' option='edit_project' />
            </div>
          </div>
        </div>
      </div>
      {/* Project Permission End */}

      {/* Task Permission Start */}
      <RenderHeader title='Tasks permissions' />
      <div className='flex w-full flex-col justify-center gap-2'>
        <div className='flex flex-col gap-4'>
          <div className='flex-row'>
            <div className='flex flex-col justify-around gap-3 lg:flex-row'>
              <RenderBody title='Can view' option='view_tasks' />
              <RenderBody title='Can edit' option='edit_tasks' />
            </div>
          </div>
        </div>
      </div>
      {/* Task Permission End */}

      {/* Roles Permission Start */}
      <RenderHeader title='Roles permissions' />
      <div className='flex w-full flex-col justify-center gap-2'>
        <div className='flex flex-col gap-4'>
          <div className='flex-row'>
            <div className='flex flex-col justify-around gap-3 lg:flex-row'>
              <div className='flex items-center gap-1'>
                <Label className='flex items-center text-xs font-semibold'>
                  {translate("Is Contractor")}
                  {permissions["contractor"] ? (
                    <CircleCheckBig
                      className={cn("h-4 w-4 text-success-500", isHebrew ? "mr-2" : "ml-2")}
                    />
                  ) : (
                    <CircleX className={cn("h-4 w-4 text-error-500", isHebrew ? "mr-2" : "ml-2")} />
                  )}
                </Label>
              </div>
              <RenderBody title='Is Supervisor' option='supervisor' showNull={false} />
            </div>
          </div>
          <div className='flex-row'>
            <div className='flex flex-col justify-around gap-3 pt-2 lg:flex-row'>
              <RenderBody title='Is Planner' option='designer' showNull={false} />
              <RenderBody title='Is Financial' option='financial' showNull={false} />
            </div>
          </div>
        </div>
      </div>

      {changeHappened ? (
        <div className='flex flex-row justify-center gap-5'>
          <Button className='w-20' variant={"outline"} onClick={resetChange}>
            {translate("Cancel")}
          </Button>
          <Button className='w-20' variant={"default"} onClick={setAccess}>
            {translate("Update")}
          </Button>
        </div>
      ) : null}
    </>
  )
}

export default ProjectPermissions
